/**
 * The branch a Team's work starts from and its pull requests open against:
 * `GET/PUT /api/repos/:repoId/pr-base`. Null is the repository's GitHub
 * default. The server says who may change it (`canEdit`), as for agent trust.
 * A response for a repository no longer shown is dropped.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { RepoPrBase, RepoPrBaseUpdateRequest } from "@yagni/shared";
import { readError } from "./agentSourcesApi.js";

export const PR_BASE_FAILED = "The branch was not saved.";
export const PR_BASE_LOAD_FAILED = "The pull request branch could not be read just now.";

function prBaseUrl(repoId: string): string {
  return `/api/repos/${encodeURIComponent(repoId)}/pr-base`;
}

export function useRepoPrBase(repoId: string | null) {
  const [prBase, setPrBase] = useState<RepoPrBase | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(repoId);
  current.current = repoId;

  useEffect(() => {
    setPrBase(null);
    setError(null);
    if (!repoId) return;
    let live = true;
    void (async () => {
      try {
        const res = await fetch(prBaseUrl(repoId), { credentials: "include" });
        if (!res.ok) {
          console.warn(`PR base: the read for repository ${repoId} answered ${res.status}.`);
          throw new Error(PR_BASE_LOAD_FAILED);
        }
        const data = (await res.json()) as Partial<RepoPrBase> | null;
        if (live) {
          setPrBase({
            branch: typeof data?.branch === "string" ? data.branch : null,
            defaultBranch: typeof data?.defaultBranch === "string" ? data.defaultBranch : "main",
            canEdit: data?.canEdit === true,
          });
        }
      } catch (err) {
        if (!(err instanceof Error && err.message === PR_BASE_LOAD_FAILED)) {
          console.warn(`PR base: the read for repository ${repoId} failed.`, err);
        }
        if (live) setError(PR_BASE_LOAD_FAILED);
      }
    })();
    return () => {
      live = false;
    };
  }, [repoId]);

  /** The branch the server stored (null: the default), or false when it refused. */
  const save = useCallback(
    async (branch: string | null): Promise<string | null | false> => {
      if (!repoId || !prBase) return false;
      const asked = repoId;
      setError(null);
      setBusy(true);
      try {
        const res = await fetch(prBaseUrl(repoId), {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ branch } satisfies RepoPrBaseUpdateRequest),
        });
        if (!res.ok) {
          console.warn(`PR base: the write for repository ${repoId} answered ${res.status}.`);
          if (current.current === asked) setError(await readError(res, PR_BASE_FAILED));
          return false;
        }
        const data = (await res.json().catch(() => null)) as Partial<RepoPrBase> | null;
        const stored = typeof data?.branch === "string" ? data.branch : null;
        if (current.current === asked) setPrBase({ ...prBase, branch: stored });
        return stored;
      } catch (err) {
        console.warn(`PR base: the write for repository ${repoId} failed.`, err);
        if (current.current === asked) setError(PR_BASE_FAILED);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [repoId, prBase],
  );

  return { prBase, busy, error, save };
}
